import { useState, useMemo, useRef, useEffect } from 'react'
import { orderSourceLabel } from '../lib/cityWorkspace'
import {
  Download,
  RefreshCw,
  AlertTriangle,
  Check,
  Printer,
  FileCode,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Users,
  Lock,
} from 'lucide-react'
import { useApp } from '../App'
import { useToastActions } from '../components/Toast'
import { airtableAPI, menusAPI } from '../lib/api'
import { buildHavnContext, mdTable, downloadMD, buildFilename } from '../lib/mdExport'

// ══════════════════════════════════════════════════════════════════════════════
// CUSTOMIZATION ORDERING — simplicity ranking
// ══════════════════════════════════════════════════════════════════════════════
//
// Orders stay grouped Glass → Plastic; WITHIN each container they're ordered
// simplest → most complex so a line cook can work top-down:
//   1 Extra protein   2 Extra carbs   3 Low carb   4 No carb
//   5 Carb-reduced mix (only low/no carb)   6 Other single change
//   7 Complex — multiple different changes; the more changes, the further down
//
// Runs off the already-parsed `affected_meals[].customization` tags (the import
// step already used AI to clean the messy Airtable text), so it's deterministic
// and sorts identically in the table, print, and export views. Anything it can't
// confidently call "simple" sinks into the complex bucket at the bottom — exactly
// where the trickiest orders belong for a manual once-over.

// Pure-dietary qualifiers — ignored when judging plating complexity, so e.g.
// "no carb, dairy free" still reads as a no-carb order.
const DIETARY_QUALIFIERS = [
  'dairy free', 'dairy-free', 'no dairy', 'gluten free', 'gluten-free',
  'low sodium', 'no eggs', 'egg free', 'vegan', 'vegetarian',
  'nut allergy', 'no nuts', 'no pork', 'halal', 'kosher',
]

const SIMPLE_CARB = ['no carb', 'no carbs', 'low carb', 'low carbs']

// Distinct plating changes for an order, dietary qualifiers stripped out.
function distinctPlatingMods(cust) {
  const mods = new Set()
  for (const m of cust.affected_meals || []) {
    for (let part of String(m.customization || '').split(',')) {
      part = part.toLowerCase().replace(/\s+/g, ' ').replace(/[.;]+$/, '').trim()
      if (!part) continue
      if (DIETARY_QUALIFIERS.some((d) => part.includes(d))) continue
      mods.add(part)
    }
  }
  return [...mods]
}

// [bucket, complexity] — lower bucket = simpler = higher in the list.
function customizationRank(cust) {
  const mods = distinctPlatingMods(cust)
  const n = mods.length
  const sole = n === 1 ? mods[0] : null
  if (sole === 'extra protein' || sole === 'extra proteins') return [1, 0]
  if (sole === 'extra carb' || sole === 'extra carbs') return [2, 0]
  if (sole === 'low carb' || sole === 'low carbs') return [3, 0]
  if (sole === 'no carb' || sole === 'no carbs') return [4, 0]
  if (n >= 1 && mods.every((x) => SIMPLE_CARB.includes(x))) return [5, 0] // mixed no/low carb
  if (n <= 1) return [6, 0]   // one other change, or dietary-only (n === 0)
  return [7, n]               // complex: more distinct changes → further down
}

// Use as the secondary comparator AFTER the Glass/Plastic split.
function compareCustomizationSimplicity(a, b) {
  const [ra, ca] = customizationRank(a)
  const [rb, cb] = customizationRank(b)
  if (ra !== rb) return ra - rb
  if (ca !== cb) return ca - cb
  return (a.customer_name || '').localeCompare(b.customer_name || '')
}

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTS & STYLING
// ══════════════════════════════════════════════════════════════════════════════

const MEAL_TYPES = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2']
const SIDE_TYPES = ['broth', 'broth_2', 'oats', 'chia', 'chia_2']
const ADDON_TYPES = ['wellness_shots', 'date_balls']

const MEAL_LABELS = {
  chicken: 'Chicken',
  chicken_2: 'Chicken 2',
  beef: 'Beef',
  seafood: 'Seafood',
  seafood_2: 'Seafood 2',
  veg: 'Veg',
  cheat: 'Cheat',
  pasta: 'Pasta',
  salad: 'Salad',
  salad_2: 'Salad 2',
  broth: 'Classic Broth',
  broth_2: 'Broth 2',
  oats: 'Overnight Oats',
  chia: 'Chia Pudding',
  chia_2: 'Chia 2',
  wellness_shots: 'Wellness Shot Bundle',
  date_balls: 'Date Balls',
}

// Card styling aligned with the app's badge system (badge-chicken, badge-beef, etc.)
const MEAL_CARD_STYLES = {
  chicken: {
    border: 'border-l-yellow-500',
    badgeClass: 'badge bg-yellow-500 text-white',
    lightBg: 'bg-yellow-50/60',
    dotColor: 'bg-yellow-500',
    printBorder: '#EAB308',
    printBg: '#FEFCE8',
  },
  chicken_2: {
    border: 'border-l-yellow-600',
    badgeClass: 'badge bg-yellow-600 text-white',
    lightBg: 'bg-yellow-100/60',
    dotColor: 'bg-yellow-600',
    printBorder: '#CA8A04',
    printBg: '#FEF3C7',
  },
  beef: {
    border: 'border-l-red-700',
    badgeClass: 'badge badge-beef',
    lightBg: 'bg-red-50/60',
    dotColor: 'bg-red-700',
    printBorder: '#B71C1C',
    printBg: '#FFEBEE',
  },
  seafood: {
    border: 'border-l-cyan-700',
    badgeClass: 'badge badge-seafood',
    lightBg: 'bg-cyan-50/60',
    dotColor: 'bg-cyan-700',
    printBorder: '#006064',
    printBg: '#E0F7FA',
  },
  seafood_2: {
    border: 'border-l-cyan-800',
    badgeClass: 'badge bg-cyan-800 text-white',
    lightBg: 'bg-cyan-100/60',
    dotColor: 'bg-cyan-800',
    printBorder: '#164e63',
    printBg: '#CFFAFE',
  },
  veg: {
    border: 'border-l-lime-600',
    badgeClass: 'badge bg-lime-600 text-white',
    lightBg: 'bg-lime-50/60',
    dotColor: 'bg-lime-600',
    printBorder: '#65A30D',
    printBg: '#ECFCCB',
  },
  cheat: {
    border: 'border-l-gray-500',
    badgeClass: 'badge bg-gray-500 text-white',
    lightBg: 'bg-gray-50/60',
    dotColor: 'bg-gray-500',
    printBorder: '#6B7280',
    printBg: '#F9FAFB',
  },
  pasta: {
    border: 'border-l-stone-500',
    badgeClass: 'badge bg-stone-500 text-white',
    lightBg: 'bg-stone-50/60',
    dotColor: 'bg-stone-500',
    printBorder: '#78716C',
    printBg: '#F5F4F2',
  },
  salad: {
    border: 'border-l-emerald-600',
    badgeClass: 'badge bg-emerald-600 text-white',
    lightBg: 'bg-emerald-50/60',
    dotColor: 'bg-emerald-600',
    printBorder: '#059669',
    printBg: '#D1FAE5',
  },
  salad_2: {
    border: 'border-l-emerald-500',
    badgeClass: 'badge bg-emerald-500 text-white',
    lightBg: 'bg-emerald-50/40',
    dotColor: 'bg-emerald-500',
    printBorder: '#10B981',
    printBg: '#ECFDF5',
  },
}

const SIDE_CARD_STYLES = {
  broth: {
    border: 'border-l-amber-700',
    badgeClass: 'badge badge-broth',
    dotColor: 'bg-amber-700',
    printBorder: '#E65100',
    printBg: '#FFF3E0',
  },
  broth_2: {
    border: 'border-l-amber-500',
    badgeClass: 'badge bg-amber-500 text-white',
    dotColor: 'bg-amber-500',
    printBorder: '#F59E0B',
    printBg: '#FFFBEB',
  },
  oats: {
    border: 'border-l-yellow-600',
    badgeClass: 'badge bg-yellow-600 text-white',
    dotColor: 'bg-yellow-600',
    printBorder: '#CA8A04',
    printBg: '#FEFCE8',
  },
  chia: {
    border: 'border-l-pink-600',
    badgeClass: 'badge badge-chia',
    dotColor: 'bg-pink-600',
    printBorder: '#AD1457',
    printBg: '#FCE4EC',
  },
  chia_2: {
    border: 'border-l-pink-400',
    badgeClass: 'badge bg-pink-400 text-white',
    dotColor: 'bg-pink-400',
    printBorder: '#F472B6',
    printBg: '#FDF2F8',
  },
}

const ADDON_CARD_STYLES = {
  wellness_shots: {
    border: 'border-l-emerald-500',
    badgeClass: 'badge bg-emerald-500 text-white',
    dotColor: 'bg-emerald-500',
    printBorder: '#10B981',
    printBg: '#ECFDF5',
  },
  date_balls: {
    border: 'border-l-amber-800',
    badgeClass: 'badge bg-amber-800 text-white',
    dotColor: 'bg-amber-800',
    printBorder: '#92400E',
    printBg: '#FEF3C7',
  },
}

// ══════════════════════════════════════════════════════════════════════════════
// MEAL COUNT CARD — One per meal type (chicken, beef, seafood, cheat)
// Hero numbers = standard glass/plastic counts (what the kitchen actually preps)
// ══════════════════════════════════════════════════════════════════════════════

function MealCountCard({ type, counts, label = null }) {
  const style = MEAL_CARD_STYLES[type]
  const displayLabel = label || MEAL_LABELS[type]
  if (!counts) return null

  const glassCust = counts.glass_custom || 0
  const plasticCust = counts.plastic_custom || 0

  return (
    <div className={`bg-white rounded-xl border border-havn-cream-300 shadow-soft overflow-hidden border-l-4 ${style.border}`}>
      {/* Header with badge */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <span className={style.badgeClass}>{displayLabel}</span>
        <span className="text-sm text-havn-gray-400 tabular-nums">
          {counts.total} total
        </span>
      </div>

      {/* Hero numbers — the primary info the kitchen needs */}
      <div className="px-5 pb-3 flex gap-3">
        {/* Glass Column */}
        <div className={`flex-1 ${style.lightBg} rounded-xl p-4 text-center`}>
          <div className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider mb-1.5">
            Glass
          </div>
          <div className="text-4xl font-display font-bold text-havn-green-900 tabular-nums leading-none">
            {counts.glass_standard}
          </div>
          <div className="text-[10px] text-havn-gray-400 uppercase tracking-wider mt-1.5">
            standard
          </div>
          {glassCust > 0 && (
            <div className="mt-2 pt-2 border-t border-black/[0.06]">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                {glassCust} custom
              </span>
            </div>
          )}
        </div>

        {/* Plastic Column */}
        <div className={`flex-1 ${style.lightBg} rounded-xl p-4 text-center`}>
          <div className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider mb-1.5">
            Plastic
          </div>
          <div className="text-4xl font-display font-bold text-havn-green-900 tabular-nums leading-none">
            {counts.plastic_standard}
          </div>
          <div className="text-[10px] text-havn-gray-400 uppercase tracking-wider mt-1.5">
            standard
          </div>
          {plasticCust > 0 && (
            <div className="mt-2 pt-2 border-t border-black/[0.06]">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                {plasticCust} custom
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Secondary info strip — total glass / plastic / all customs */}
      <div className="px-5 py-2.5 bg-havn-cream-50 border-t border-havn-cream-200 flex items-center gap-4 text-xs text-havn-gray-500">
        <span>Glass: <strong className="text-havn-charcoal">{counts.glass}</strong></span>
        <span>Plastic: <strong className="text-havn-charcoal">{counts.plastic}</strong></span>
        <span className="ml-auto">Custom: <strong className="text-havn-charcoal">{counts.total_custom || 0}</strong></span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SIDE COUNT CARD — Simpler card for broth/chia (no glass/plastic split)
// ══════════════════════════════════════════════════════════════════════════════

function SideCountCard({ type, counts }) {
  const style = SIDE_CARD_STYLES[type]
  const label = MEAL_LABELS[type]
  if (!counts) return null

  return (
    <div className={`bg-white rounded-xl border border-havn-cream-300 shadow-soft overflow-hidden border-l-4 ${style.border} p-5 flex items-center gap-4`}>
      <span className={style.badgeClass}>{label}</span>
      <div className="ml-auto text-right">
        <div className="text-3xl font-display font-bold text-havn-green-900 tabular-nums leading-none">
          {counts.total}
        </div>
        <div className="text-[10px] text-havn-gray-400 uppercase tracking-wider mt-1">
          total
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ADDON COUNT CARD — For wellness shot bundles (shows bundles + derived shots)
// ══════════════════════════════════════════════════════════════════════════════

function AddonCountCard({ type, counts }) {
  const style = ADDON_CARD_STYLES[type]
  const label = MEAL_LABELS[type]
  if (!counts || !counts.total) return null

  const bundles = counts.total
  const totalShots = bundles * 4
  const totalOz = totalShots * 2

  return (
    <div className={`bg-white rounded-xl border border-havn-cream-300 shadow-soft overflow-hidden border-l-4 ${style.border}`}>
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <span className={style.badgeClass}>{label}</span>
        <span className="text-sm text-havn-gray-400 tabular-nums">
          {bundles} {bundles === 1 ? 'bundle' : 'bundles'}
        </span>
      </div>
      <div className="px-5 pb-3">
        <div className="text-4xl font-display font-bold text-havn-green-900 tabular-nums leading-none">
          {bundles}
        </div>
      </div>
      <div className="px-5 py-2.5 bg-emerald-50 border-t border-havn-cream-200 flex items-center gap-4 text-xs text-havn-gray-500">
        <span>Shots: <strong className="text-havn-charcoal">{totalShots}</strong> <span className="text-[10px]">(4 per bundle)</span></span>
        <span className="ml-auto">Volume: <strong className="text-havn-charcoal">{totalOz} oz</strong> <span className="text-[10px]">(2 oz each)</span></span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TOTALS SUMMARY BAR — Dark green bar with grand totals
// ══════════════════════════════════════════════════════════════════════════════

function TotalsSummaryBar({ totals }) {
  if (!totals) return null

  return (
    <div className="bg-havn-green-800 rounded-xl p-5 flex items-center justify-around text-white">
      <div className="text-center px-3">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Glass</div>
        <div className="text-2xl font-display font-bold tabular-nums">{totals.glass}</div>
      </div>
      <div className="w-px h-10 bg-white/20" />
      <div className="text-center px-3">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Plastic</div>
        <div className="text-2xl font-display font-bold tabular-nums">{totals.plastic}</div>
      </div>
      <div className="w-px h-10 bg-white/20" />
      <div className="text-center px-3">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Meals</div>
        <div className="text-2xl font-display font-bold tabular-nums">{totals.total_meals}</div>
      </div>
      <div className="w-px h-10 bg-white/20" />
      <div className="text-center px-3">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Sides</div>
        <div className="text-2xl font-display font-bold tabular-nums">{totals.total_sides}</div>
      </div>
      {totals.total_wellness_bundles > 0 && (
        <>
          <div className="w-px h-10 bg-white/20" />
          <div className="text-center px-3">
            <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Shot Bundles</div>
            <div className="text-2xl font-display font-bold tabular-nums">{totals.total_wellness_bundles}</div>
          </div>
        </>
      )}
      <div className="w-px h-10 bg-white/20" />
      <div className="text-center px-4">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Total All</div>
        <div className="text-3xl font-display font-bold tabular-nums">{totals.total_meals_and_sides}</div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MEAL COUNTS SECTION — Wraps cards + sides + totals bar
// ══════════════════════════════════════════════════════════════════════════════

function MealCountsSection({ mealCounts, totals, dishLabels }) {
  if (!mealCounts) return null

  return (
    <div className="space-y-4">
      {/* Main meal cards — 2×2 grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MEAL_TYPES.map((type) => (
          <MealCountCard key={type} type={type} counts={mealCounts[type]} label={dishLabels?.[type] || MEAL_LABELS[type]} />
        ))}
      </div>

      {/* Side cards — 2 across */}
      <div className="grid grid-cols-2 gap-4">
        {SIDE_TYPES.map((type) => (
          <SideCountCard key={type} type={type} counts={mealCounts[type]} />
        ))}
      </div>

      {/* Add-On cards (wellness shots) */}
      {ADDON_TYPES.some(type => mealCounts[type]?.total > 0) && (
        <div className="grid grid-cols-2 gap-4">
          {ADDON_TYPES.map((type) => (
            <AddonCountCard key={type} type={type} counts={mealCounts[type]} />
          ))}
        </div>
      )}

      {/* Grand totals bar */}
      <TotalsSummaryBar totals={totals} />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// CUSTOMIZATIONS TABLE — Redesigned with grouping, zebra stripes, colored badges
// ══════════════════════════════════════════════════════════════════════════════

function CustomizationsTable({ customizations, dishLabels }) {
  // Filter out customizations with no relevant content (admin notes only)
  // Keep rows that have: affected meals, allergy alerts, or side notes
  const filtered = useMemo(() => {
    if (!customizations) return []
    return customizations.filter((cust) => {
      const meals = cust.affected_meals || []
      const hasAffectedMeals = meals.reduce((sum, m) => sum + (m.quantity || 0), 0) > 0
      const hasAllergyAlert = !!cust.allergy_alert
      const hasSideNotes = !!cust.side_notes
      return hasAffectedMeals || hasAllergyAlert || hasSideNotes
    })
  }, [customizations])

  if (filtered.length === 0) {
    return (
      <div className="text-center py-8 text-havn-gray-400">
        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No customizations this week</p>
      </div>
    )
  }

  // Sort: Glass first, then Plastic, then alphabetical by name
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const order = { Glass: 0, Plastic: 1 }
      const aOrder = order[a.container_type] ?? 2
      const bOrder = order[b.container_type] ?? 2
      if (aOrder !== bOrder) return aOrder - bOrder
      return compareCustomizationSimplicity(a, b)
    })
  }, [customizations])

  // Find where glass ends and plastic starts for group headers
  const groups = useMemo(() => {
    const result = []
    let currentContainer = null
    sorted.forEach((cust, idx) => {
      if (cust.container_type !== currentContainer) {
        currentContainer = cust.container_type
        result.push({ type: 'header', container: currentContainer, startIdx: idx })
      }
      result.push({ type: 'row', data: cust, idx })
    })
    return result
  }, [sorted])

  // Column totals
  const columnTotals = useMemo(() => {
    const totals = { chicken: 0, chicken_2: 0, beef: 0, seafood: 0, seafood_2: 0, veg: 0, cheat: 0, pasta: 0, salad: 0, salad_2: 0, total: 0 }
    filtered.forEach((cust) => {
      ;(cust.affected_meals || []).forEach((m) => {
        const type = m.meal_type?.toLowerCase()
        if (type && totals[type] !== undefined) {
          totals[type] += m.quantity || 0
          totals.total += m.quantity || 0
        }
      })
    })
    return totals
  }, [filtered])

  // Count by container type
  const containerCounts = useMemo(() => {
    const counts = {}
    filtered.forEach((c) => {
      counts[c.container_type] = (counts[c.container_type] || 0) + 1
    })
    return counts
  }, [filtered])

  let rowCounter = 0

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-havn-cream-100">
            <th className="text-left py-3 px-4 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider border-b-2 border-havn-green-700 min-w-[150px]">
              Customer
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider border-b-2 border-havn-green-700 min-w-[280px]">
              Customization
            </th>
            {MEAL_TYPES.map((type) => (
              <th
                key={type}
                className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-wider border-b-2 border-havn-green-700 w-[72px]"
              >
                <div className="flex items-center justify-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${MEAL_CARD_STYLES[type].dotColor}`} />
                  <span className="text-havn-gray-500">{dishLabels?.[type] || MEAL_LABELS[type]}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((item, gIdx) => {
            if (item.type === 'header') {
              return (
                <tr key={`header-${item.container}`} className="bg-havn-cream-200/60 border-y border-havn-cream-300">
                  <td colSpan={10} className="py-2 px-4">
                    <span className="text-xs font-semibold text-havn-gray-600 uppercase tracking-wider">
                      {item.container} Containers
                    </span>
                    <span className="text-xs text-havn-gray-400 ml-2">
                      ({containerCounts[item.container]} customer{containerCounts[item.container] !== 1 ? 's' : ''})
                    </span>
                  </td>
                </tr>
              )
            }

            const cust = item.data
            const isEven = rowCounter % 2 === 0
            rowCounter++

            // Build affected meals lookup
            const affected = {}
            ;(cust.affected_meals || []).forEach((m) => {
              const t = m.meal_type?.toLowerCase()
              if (t) affected[t] = { quantity: m.quantity || 0, customization: m.customization || '' }
            })

            return (
              <tr
                key={`row-${gIdx}`}
                className={`border-b border-havn-cream-200 ${isEven ? 'bg-white' : 'bg-havn-cream-50'} hover:bg-havn-cream-100/50 transition-colors`}
              >
                <td className="py-3 px-4">
                  <div className="font-semibold text-sm text-havn-charcoal">{cust.customer_name}</div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm text-havn-charcoal font-medium whitespace-pre-wrap break-words max-w-sm">
                    {cust.summary || cust.raw_instructions}
                  </div>
                  {cust.allergy_alert && (
                    <div className="mt-1.5 px-2.5 py-1.5 bg-red-50 border-2 border-red-400 rounded-md text-xs font-bold text-red-800">
                      ⚠️ ALLERGY: {cust.allergy_alert}
                    </div>
                  )}
                  {cust.side_notes && (
                    <div className="mt-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 italic">
                      Sides: {cust.side_notes}
                    </div>
                  )}
                </td>
                {MEAL_TYPES.map((type) => {
                  const meal = affected[type]
                  const cardStyle = MEAL_CARD_STYLES[type]
                  return (
                    <td key={type} className="text-center py-3 px-3">
                      {meal ? (
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${cardStyle.badgeClass}`}>
                          {meal.quantity}
                        </span>
                      ) : (
                        <span className="text-havn-gray-200">—</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}

          {/* Totals Row */}
          <tr className="border-t-2 border-havn-green-700 bg-havn-cream-100">
            <td className="py-3 px-4 font-bold text-sm text-havn-charcoal">
              Total
            </td>
            <td className="py-3 px-4 text-sm text-havn-gray-500">
              {columnTotals.total} customized meals
            </td>
            {MEAL_TYPES.map((type) => {
              const cardStyle = MEAL_CARD_STYLES[type]
              return (
                <td key={type} className="text-center py-3 px-3">
                  {columnTotals[type] ? (
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${cardStyle.badgeClass}`}>
                      {columnTotals[type]}
                    </span>
                  ) : (
                    <span className="text-havn-gray-200">—</span>
                  )}
                </td>
              )
            })}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// FLAGGED ITEMS ALERT
// ══════════════════════════════════════════════════════════════════════════════

function FlaggedItems({ items }) {
  const [expanded, setExpanded] = useState(false)

  if (!items || items.length === 0) return null

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 shadow-soft">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left"
      >
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <span className="font-semibold text-amber-900">
          {items.length} customization{items.length > 1 ? 's' : ''} flagged for review
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 ml-auto text-amber-600" /> : <ChevronDown className="w-4 h-4 ml-auto text-amber-600" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3 transition-all duration-200">
          {items.map((item, idx) => (
            <div key={idx} className="bg-white rounded-lg p-3 border border-amber-100">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm text-havn-charcoal">{item.customer_name}</span>
                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{item.container_type}</span>
              </div>
              <p className="text-sm text-havn-gray-500 mb-1">"{item.raw_instructions}"</p>
              {item.flag_reason && (
                <p className="text-xs text-amber-600 italic">{item.flag_reason}</p>
              )}
            </div>
          ))}
          <p className="text-xs text-amber-700 italic">
            Please verify these customizations manually before printing.
          </p>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// PRINT VIEW — Clean, ink-efficient, card-based layout
// ══════════════════════════════════════════════════════════════════════════════

function openPrintView(data, dishLabels) {
  const win = window.open('', '_blank')
  if (!win) return

  const mc = data.meal_counts
  const totals = data.totals

  // Include ALL customizations that have affected meals, allergy alerts, or side notes
  const customizations = (data.customizations || []).filter((cust) => {
    const meals = cust.affected_meals || []
    const hasAffectedMeals = meals.reduce((sum, m) => sum + (m.quantity || 0), 0) > 0
    const hasAllergyAlert = !!cust.allergy_alert
    const hasSideNotes = !!cust.side_notes
    return hasAffectedMeals || hasAllergyAlert || hasSideNotes
  })

  // Sort customizations: Glass first, then Plastic, then simplest -> most complex
  const sorted = [...customizations].sort((a, b) => {
    const order = { Glass: 0, Plastic: 1 }
    const aO = order[a.container_type] ?? 2
    const bO = order[b.container_type] ?? 2
    if (aO !== bO) return aO - bO
    return compareCustomizationSimplicity(a, b)
  })

  // Color definitions — used for colored squares behind numbers
  const mealTypes = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2']
  const mealLabelsMap = {
    chicken: dishLabels?.chicken || 'Chicken',
    chicken_2: dishLabels?.chicken_2 || 'Chicken 2',
    beef: dishLabels?.beef || 'Beef',
    seafood: dishLabels?.seafood || 'Seafood',
    seafood_2: dishLabels?.seafood_2 || 'Seafood 2',
    veg: dishLabels?.veg || 'Veg',
    cheat: dishLabels?.cheat || 'Cheat',
    pasta: dishLabels?.pasta || 'Pasta',
    salad: dishLabels?.salad || 'Salad',
    salad_2: dishLabels?.salad_2 || 'Salad 2',
  }
  const printColors = {
    chicken: { border: '#EAB308', bg: '#FEF9C3', headerBg: '#FDE047' },
    chicken_2: { border: '#CA8A04', bg: '#FEF3C7', headerBg: '#FACC15' },
    beef: { border: '#B71C1C', bg: '#FECACA', headerBg: '#F87171' },
    seafood: { border: '#006064', bg: '#A5F3FC', headerBg: '#22D3EE' },
    seafood_2: { border: '#164e63', bg: '#CFFAFE', headerBg: '#06B6D4' },
    veg: { border: '#65A30D', bg: '#ECFCCB', headerBg: '#84CC16' },
    cheat: { border: '#6B7280', bg: '#E5E7EB', headerBg: '#9CA3AF' },
    pasta: { border: '#78716C', bg: '#F5F4F2', headerBg: '#A8A29E' },
    salad: { border: '#059669', bg: '#D1FAE5', headerBg: '#34D399' },
    salad_2: { border: '#10B981', bg: '#ECFDF5', headerBg: '#6EE7B7' },
  }

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 1: MEAL COUNTS — Big, bold, fills the page
  // ═══════════════════════════════════════════════════════════════════

  // 2+3+3 grid class assignment on a 6-col grid:
  //   row 1 (featured): cheat + pasta → span 3 each = 6
  //   row 2 (mid):      chicken + chicken_2 + beef → span 2 each = 6
  //   row 3 (bot):      seafood + seafood_2 + veg → span 2 each = 6
  const gridClassMap = { cheat: 'card-featured', pasta: 'card-featured',
    chicken: 'card-mid', chicken_2: 'card-mid', beef: 'card-mid',
    seafood: 'card-bot', seafood_2: 'card-bot', veg: 'card-bot',
    salad: 'card-bot', salad_2: 'card-bot' }

  let mealCardsHTML = mealTypes.map((type) => {
    const c = mc[type]
    if (!c) return ''
    const pc = printColors[type]
    const glassCust = c.glass_custom || 0
    const plasticCust = c.plastic_custom || 0
    const gridClass = gridClassMap[type] || 'card-mid'

    // Custom pill HTML — only shown when there are customs
    const glassCustomHTML = glassCust > 0
      ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,0.06)"><span style="display:inline-block;background:#FEF3C7;color:#92400E;font-size:12px;font-weight:700;padding:2px 10px;border-radius:10px;font-family:'DM Sans',sans-serif">${glassCust} custom</span></div>`
      : ''
    const plasticCustomHTML = plasticCust > 0
      ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,0.06)"><span style="display:inline-block;background:#FEF3C7;color:#92400E;font-size:12px;font-weight:700;padding:2px 10px;border-radius:10px;font-family:'DM Sans',sans-serif">${plasticCust} custom</span></div>`
      : ''

    return `
      <div class="${gridClass}" style="border:2px solid ${pc.border};border-radius:12px;padding:20px;break-inside:avoid">
        <div style="font-weight:800;font-size:20px;margin-bottom:16px;color:#1C1C1A;text-transform:uppercase;letter-spacing:2px;font-family:'DM Sans',sans-serif;text-align:center;background:${pc.headerBg};margin:-20px -20px 16px;padding:14px;border-radius:10px 10px 0 0">${mealLabelsMap[type]}</div>
        <div style="display:flex;gap:16px;margin-bottom:14px">
          <div style="flex:1;background:${pc.bg};border-radius:10px;padding:16px;text-align:center">
            <div style="font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#555;margin-bottom:6px;font-family:'DM Sans',sans-serif;font-weight:600">Glass</div>
            <div style="font-size:52px;font-weight:900;color:#1C1C1A;font-family:'Cormorant Garamond',serif;line-height:1">${c.glass_standard}</div>
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-top:4px;font-family:'DM Sans',sans-serif">standard</div>
            ${glassCustomHTML}
          </div>
          <div style="flex:1;background:${pc.bg};border-radius:10px;padding:16px;text-align:center">
            <div style="font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#555;margin-bottom:6px;font-family:'DM Sans',sans-serif;font-weight:600">Plastic</div>
            <div style="font-size:52px;font-weight:900;color:#1C1C1A;font-family:'Cormorant Garamond',serif;line-height:1">${c.plastic_standard}</div>
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-top:4px;font-family:'DM Sans',sans-serif">standard</div>
            ${plasticCustomHTML}
          </div>
        </div>
        <div style="border-top:2px solid ${pc.bg};padding-top:10px;font-size:14px;color:#333;display:flex;justify-content:space-around;font-family:'DM Sans',sans-serif;font-weight:600">
          <span>Total: <strong style="color:#1C1C1A;font-size:16px">${c.total}</strong></span>
          <span>Custom: <strong style="color:#1C1C1A;font-size:16px">${c.total_custom || 0}</strong></span>
        </div>
      </div>`
  }).join('')

  // Side cards — render every side slot present in this week's data
  const sideLabelsMap = {
    broth: 'Classic Broth',
    broth_2: 'Broth 2',
    oats: 'Overnight Oats',
    chia: 'Chia Pudding',
    chia_2: 'Chia 2',
  }
  const sidePrintColors = {
    broth:   { border: '#E65100', bg: '#FFF3E0', headerBg: '#FB923C' },
    broth_2: { border: '#F59E0B', bg: '#FFFBEB', headerBg: '#FBBF24' },
    oats:    { border: '#CA8A04', bg: '#FEFCE8', headerBg: '#EAB308' },
    chia:    { border: '#AD1457', bg: '#FCE4EC', headerBg: '#F472B6' },
    chia_2:  { border: '#F472B6', bg: '#FDF2F8', headerBg: '#F9A8D4' },
  }

  let sideCardsHTML = ['broth', 'broth_2', 'oats', 'chia', 'chia_2'].map((type) => {
    const c = mc[type]
    if (!c) return ''
    const pc = sidePrintColors[type]
    return `
      <div style="border:2px solid ${pc.border};border-radius:12px;padding:20px;display:flex;align-items:center;justify-content:space-between">
        <span style="font-weight:800;font-size:18px;color:#1C1C1A;text-transform:uppercase;letter-spacing:2px;font-family:'DM Sans',sans-serif;background:${pc.headerBg};padding:8px 16px;border-radius:8px">${sideLabelsMap[type]}</span>
        <div style="text-align:right">
          <div style="font-size:52px;font-weight:900;color:#1C1C1A;font-family:'Cormorant Garamond',serif;line-height:1">${c.total}</div>
          <div style="font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-top:4px;font-family:'DM Sans',sans-serif;font-weight:600">total</div>
        </div>
      </div>`
  }).join('')

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 2+: CUSTOMIZATIONS — Bigger font, colored squares for numbers
  // ═══════════════════════════════════════════════════════════════════

  let customsHTML = ''
  const colTotals = { chicken: 0, chicken_2: 0, beef: 0, seafood: 0, seafood_2: 0, veg: 0, cheat: 0, pasta: 0, salad: 0, salad_2: 0 }
  let currentContainer = null

  sorted.forEach((cust, idx) => {
    // Group header
    if (cust.container_type !== currentContainer) {
      currentContainer = cust.container_type
      customsHTML += `<tr><td colspan="${mealTypes.length + 2}" style="background:#E5E0D6;padding:10px 14px;font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#333;border-top:3px solid #1B5E40;font-family:'DM Sans',sans-serif">${currentContainer} Containers</td></tr>`
    }

    const affected = {}
    ;(cust.affected_meals || []).forEach((m) => {
      const t = m.meal_type?.toLowerCase()
      if (t) {
        affected[t] = m.quantity || 0
        if (colTotals[t] !== undefined) colTotals[t] += m.quantity || 0
      }
    })

    const bg = idx % 2 === 0 ? '#fff' : '#fafaf8'

    // Build customization text with allergy alert and side notes
    let custText = cust.summary || cust.raw_instructions
    let allergyHTML = ''
    let sideNoteHTML = ''

    if (cust.allergy_alert) {
      allergyHTML = `<div style="background:#FEE2E2;border:2px solid #EF4444;border-radius:6px;padding:6px 10px;margin-top:6px;font-size:13px;font-weight:700;color:#991B1B">&#9888; ALLERGY: ${cust.allergy_alert}</div>`
    }
    if (cust.side_notes) {
      sideNoteHTML = `<div style="background:#F0F9FF;border:1px solid #93C5FD;border-radius:4px;padding:4px 8px;margin-top:4px;font-size:12px;color:#1E40AF;font-style:italic">Sides: ${cust.side_notes}</div>`
    }

    customsHTML += `<tr style="background:${bg}">
      <td style="padding:10px 14px;font-weight:700;font-size:15px;border-bottom:1px solid #E5E0D6;vertical-align:top">${cust.customer_name}</td>
      <td style="padding:10px 14px;font-size:14px;border-bottom:1px solid #E5E0D6;vertical-align:top;max-width:340px">${custText}${allergyHTML}${sideNoteHTML}</td>
      ${mealTypes.map((type) => {
        const pc = printColors[type]
        const qty = affected[type]
        if (qty) {
          return `<td style="text-align:center;padding:10px 6px;border-bottom:1px solid #E5E0D6;vertical-align:top"><div style="display:inline-block;background:${pc.bg};border:2px solid ${pc.border};border-radius:8px;width:42px;height:42px;line-height:38px;font-weight:900;font-size:18px;color:#1C1C1A">${qty}</div></td>`
        }
        return `<td style="text-align:center;padding:10px 6px;border-bottom:1px solid #E5E0D6;vertical-align:top;color:#ddd;font-size:14px">&mdash;</td>`
      }).join('')}
    </tr>`
  })

  const totalCustoms = Object.values(colTotals).reduce((a, b) => a + b, 0)

  const html = `<!DOCTYPE html>
<html>
<head>
<title>Kitchen Production Sheet — ${data.city_id?.toUpperCase() || ''} — Week of ${data.week_of || 'Current'}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'DM Sans', 'Helvetica Neue', Arial, sans-serif; color: #1C1C1A; padding: 24px; background: #fff; }
  h1 { font-family: 'Cormorant Garamond', serif; font-size: 36px; font-weight: 700; margin-bottom: 4px; }
  h2 { font-family: 'Cormorant Garamond', serif; font-size: 28px; font-weight: 700; margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 3px solid #1B5E40; }
  .meta { color: #666; font-size: 14px; margin-bottom: 24px; font-family: 'DM Sans', sans-serif; }
  .cards-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 16px; margin-bottom: 20px; }
  /* 6-col grid: card-mid spans 2 (chicken/chicken_2/beef), card-bot + card-featured span 3
     (seafood/seafood_2/veg/salad/salad_2 + cheat/pasta) */
  .cards-grid .card-featured { grid-column: span 3; }
  .cards-grid .card-mid { grid-column: span 2; }
  .cards-grid .card-bot { grid-column: span 3; }
  .sides-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
  .totals-bar { background: #122620; border-radius: 12px; padding: 20px 24px; display: flex; align-items: center; justify-content: space-around; color: white; margin-bottom: 24px; }
  .totals-bar .stat { text-align: center; }
  .totals-bar .stat-label { font-size: 12px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.7; margin-bottom: 4px; font-family: 'DM Sans', sans-serif; font-weight: 600; }
  .totals-bar .stat-value { font-size: 32px; font-weight: 900; font-family: 'Cormorant Garamond', serif; }
  .totals-bar .stat-value.grand { font-size: 42px; }
  .totals-bar .divider { width: 1px; height: 40px; background: rgba(255,255,255,0.2); }
  table { width: 100%; border-collapse: collapse; }
  th { padding: 12px 14px; text-align: left; font-size: 13px; text-transform: uppercase; letter-spacing: 2px; color: #5C5A55; border-bottom: 3px solid #1B5E40; font-weight: 700; font-family: 'DM Sans', sans-serif; }
  @media print {
    body { padding: 16px; }
    .page-break { page-break-before: always; }
    @page { margin: 0.4in; }
  }
</style>
</head>
<body>
  <!-- ═══════════════ PAGE 1: MEAL COUNTS ═══════════════ -->
  <h1>Kitchen Production Sheet</h1>
  <p class="meta">${data.city_id?.toUpperCase() || ''} &mdash; Week of ${data.week_of || 'Current'} &mdash; ${data.order_count || 0} ${importScopeLabel(data)}orders &mdash; Imported ${new Date(data.imported_at).toLocaleString()}</p>

  <h2>Meal Counts</h2>
  <div class="cards-grid">${mealCardsHTML}</div>
  <div class="sides-grid">${sideCardsHTML}</div>
  <div class="totals-bar">
    <div class="stat"><div class="stat-label">Glass</div><div class="stat-value">${totals?.glass || 0}</div></div>
    <div class="divider"></div>
    <div class="stat"><div class="stat-label">Plastic</div><div class="stat-value">${totals?.plastic || 0}</div></div>
    <div class="divider"></div>
    <div class="stat"><div class="stat-label">Meals</div><div class="stat-value">${totals?.total_meals || 0}</div></div>
    <div class="divider"></div>
    <div class="stat"><div class="stat-label">Sides</div><div class="stat-value">${totals?.total_sides || 0}</div></div>
    <div class="divider"></div>
    <div class="stat"><div class="stat-label">Total All</div><div class="stat-value grand">${totals?.total_meals_and_sides || 0}</div></div>
  </div>

  <!-- ═══════════════ PAGE 2+: CUSTOMIZATIONS ═══════════════ -->
  <div class="page-break"></div>

  <h1 style="margin-bottom:2px">Customizations</h1>
  <p class="meta">${data.city_id?.toUpperCase() || ''} &mdash; Week of ${data.week_of || 'Current'} &mdash; ${customizations.length} orders with customizations</p>

  <table>
    <thead>
      <tr>
        <th style="min-width:140px">Customer</th>
        <th style="min-width:220px">Customization</th>
        ${mealTypes.map((type) => {
          const pc = printColors[type]
          return `<th style="width:70px;text-align:center"><div style="display:inline-block;background:${pc.headerBg};border-radius:6px;padding:4px 10px;color:#1C1C1A;font-weight:800">${mealLabelsMap[type].slice(0,2).toUpperCase()}</div></th>`
        }).join('')}
      </tr>
    </thead>
    <tbody>
      ${customsHTML}
      <tr style="border-top:3px solid #1B5E40;font-weight:700">
        <td style="padding:12px 14px;font-size:15px;font-weight:800">Total</td>
        <td style="padding:12px 14px;font-size:14px;color:#555;font-weight:600">${totalCustoms} customized meals</td>
        ${mealTypes.map((type) => {
          const pc = printColors[type]
          const val = colTotals[type]
          if (val) {
            return `<td style="text-align:center;padding:12px 6px"><div style="display:inline-block;background:${pc.bg};border:2px solid ${pc.border};border-radius:8px;width:42px;height:42px;line-height:38px;font-weight:900;font-size:18px;color:#1C1C1A">${val}</div></td>`
          }
          return `<td style="text-align:center;padding:12px 6px;color:#ddd;font-size:14px">&mdash;</td>`
        }).join('')}
      </tr>
    </tbody>
  </table>
</body>
</html>`

  win.document.write(html)
  win.document.close()
}

// ══════════════════════════════════════════════════════════════════════════════
// MARKDOWN EXPORT — LLM-optimized version of the production sheet
// ══════════════════════════════════════════════════════════════════════════════

function buildProductionSheetMD(data, dishLabels) {
  const mc = data.meal_counts || {}
  const totals = data.totals || {}
  const city = data.city_id
  const importedAt = data.imported_at ? new Date(data.imported_at).toISOString() : ''
  const mealTypes = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2']
  const sideTypes = ['broth', 'oats']

  const labelFor = (type) => {
    const dishName = dishLabels?.[type]
    const slotLabel = MEAL_LABELS[type] || type
    return dishName ? `${dishName} (${slotLabel})` : slotLabel
  }

  let md = buildHavnContext({
    docType: 'Kitchen Production Sheet',
    docPurpose: `Meal counts (by slot + container) and per-order customizations imported from ${data.source === 'havn_os' ? 'Havn OS' : 'Airtable'}. Source of truth for what the kitchen plates this week.`,
    city,
    menu: data.menu_name || undefined,
    weekOf: data.week_of,
  })

  md += `# Kitchen Production Sheet\n\n`
  md += `- **Orders imported:** ${data.order_count || 0}${data.source === 'havn_os' ? ` (${data.scope === 'paid' ? 'paid orders only' : 'every confirmed order'})` : ''}\n`
  md += `- **Customizations:** ${data.customization_count || 0}\n`
  md += `- **Imported at:** ${importedAt}\n`
  if (data.week_of) md += `- **Week of:** ${data.week_of}\n`
  md += `\n`

  // ────────────────────────────────────────────────────────────────────────
  // Meal counts
  // ────────────────────────────────────────────────────────────────────────
  md += `## Meal Counts\n\n`
  md += `> "Standard" = Glass + Plastic counts the kitchen plates normally. "Custom" = orders with modifications, plated separately by hand and NOT included in standard.\n\n`

  const mealRows = mealTypes
    .filter((t) => mc[t])
    .map((t) => {
      const c = mc[t]
      return [
        labelFor(t),
        c.glass_standard ?? 0,
        c.plastic_standard ?? 0,
        c.glass_custom ?? 0,
        c.plastic_custom ?? 0,
        c.total ?? 0,
        c.total_custom ?? 0,
      ]
    })
  if (mealRows.length) {
    md += mdTable(
      ['Slot (Dish)', 'Glass Std', 'Plastic Std', 'Glass Custom', 'Plastic Custom', 'Total', 'Total Custom'],
      mealRows,
    )
    md += `\n\n`
  }

  const sideRows = sideTypes
    .filter((t) => mc[t])
    .map((t) => {
      const c = mc[t]
      const label = t === 'broth' ? 'Classic Broth' : t === 'oats' ? 'Chia Pudding' : t
      return [label, c.total ?? 0]
    })
  if (sideRows.length) {
    md += `### Sides\n\n`
    md += mdTable(['Side', 'Total'], sideRows)
    md += `\n\n`
  }

  // Grand totals
  if (Object.keys(totals).length) {
    md += `### Totals\n\n`
    md += mdTable(
      ['Glass', 'Plastic', 'Total Meals', 'Total Sides', 'Grand Total'],
      [[
        totals.glass ?? 0,
        totals.plastic ?? 0,
        totals.total_meals ?? 0,
        totals.total_sides ?? 0,
        totals.total_meals_and_sides ?? 0,
      ]],
    )
    md += `\n\n`
  }

  // ────────────────────────────────────────────────────────────────────────
  // Flagged items
  // ────────────────────────────────────────────────────────────────────────
  const flagged = data.flagged_items || []
  if (flagged.length) {
    md += `## Flagged Customizations (Need Manual Review)\n\n`
    md += `> The AI classifier flagged these and couldn't auto-parse them. Verify manually before plating.\n\n`
    md += mdTable(
      ['Customer', 'Container', 'Raw Instruction', 'Flag Reason'],
      flagged.map((f) => [
        f.customer_name || '',
        f.container_type || '',
        f.raw_instructions || '',
        f.flag_reason || '',
      ]),
    )
    md += `\n\n`
  }

  // ────────────────────────────────────────────────────────────────────────
  // Customizations (full list)
  // ────────────────────────────────────────────────────────────────────────
  const customizations = (data.customizations || []).filter((c) => {
    const affected = c.affected_meals || []
    const total = affected.reduce((s, m) => s + (m.quantity || 0), 0)
    return total > 0 || c.allergy_alert || c.side_notes
  })

  if (customizations.length) {
    // Sort Glass first, then Plastic, then alpha
    const sorted = [...customizations].sort((a, b) => {
      const order = { Glass: 0, Plastic: 1 }
      const ac = order[a.container_type] ?? 2
      const bc = order[b.container_type] ?? 2
      if (ac !== bc) return ac - bc
      return compareCustomizationSimplicity(a, b)
    })

    md += `## Customizations (${sorted.length})\n\n`
    md += `> Container: Glass = premium reusable (most customers), Plastic = single-use. Each row lists which meal slots are affected and by how many.\n\n`

    md += mdTable(
      ['Customer', 'Container', 'Instruction', 'Affects (slot × qty)', 'Allergy', 'Side Notes'],
      sorted.map((c) => {
        const affected = (c.affected_meals || [])
          .filter((m) => (m.quantity || 0) > 0)
          .map((m) => `${m.meal_type}×${m.quantity}`)
          .join(', ')
        return [
          c.customer_name || '',
          c.container_type || '',
          (c.summary || c.raw_instructions || '').trim(),
          affected,
          c.allergy_alert || '',
          c.side_notes || '',
        ]
      }),
    )
    md += `\n\n`

    // Per-slot customization totals
    const colTotals = {}
    sorted.forEach((c) => {
      ;(c.affected_meals || []).forEach((m) => {
        const t = (m.meal_type || '').toLowerCase()
        if (!t) return
        colTotals[t] = (colTotals[t] || 0) + (m.quantity || 0)
      })
    })
    const totalsByType = mealTypes
      .filter((t) => colTotals[t])
      .map((t) => [labelFor(t), colTotals[t]])
    if (totalsByType.length) {
      md += `### Customized Meals by Slot\n\n`
      md += mdTable(['Slot (Dish)', 'Customized Qty'], totalsByType)
      md += `\n\n`
    }
  }

  return md
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PRODUCTION SHEET COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

// "paid " / "confirmed " for Havn OS imports; imports saved before the paid option counted every confirmed order.
function importScopeLabel(data) {
  if (data?.source !== 'havn_os') return ''
  return data.scope === 'paid' ? 'paid ' : 'confirmed '
}

const LOCKED_TITLE = 'Counts are locked for this city; ask Zeshan to refresh them'

function ImportButton({ onClick, disabled, busy, label, title }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} className="btn-primary flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50">
      {busy ? (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" />
          Importing...
        </>
      ) : (
        <>
          <Download className="w-4 h-4" />
          {label}
        </>
      )}
    </button>
  )
}

export default function ProductionSheet({ onImportMealCounts }) {
  const { currentCity, currentMenu } = useApp()
  const sourceLabel = orderSourceLabel(currentCity)
  // Havn OS cities can count every confirmed order or only the paid ones.
  const canScope = sourceLabel === 'Havn OS'
  const { success, error: showError } = useToastActions()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingScope, setLoadingScope] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [progress, setProgress] = useState('')
  const [customsExpanded, setCustomsExpanded] = useState(true)
  const [dishLabels, setDishLabels] = useState(null)
  // Locked cities keep their last import; the server refuses a new one too.
  const [importLocked, setImportLocked] = useState(false)

  useEffect(() => {
    let live = true
    setImportLocked(false)
    if (currentCity) airtableAPI.getImportLock(currentCity).then(r => { if (live) setImportLocked(Boolean(r?.locked)) }).catch(() => {})
    return () => { live = false }
  }, [currentCity])

  // Keep a ref to the latest callback so the async import always calls the current version
  const onImportRef = useRef(onImportMealCounts)
  useEffect(() => {
    onImportRef.current = onImportMealCounts
  }, [onImportMealCounts])

  // Fetch dish names for the current menu to use as column labels
  useEffect(() => {
    if (!currentMenu) { setDishLabels(null); return }
    menusAPI.get(currentMenu).then((menu) => {
      const labels = {}
      ;(menu.dishes || []).forEach((d) => {
        const cat = d.category
        if (cat && MEAL_TYPES.includes(cat)) {
          // Strip " with ..." sauce/component descriptions from the label
          labels[cat] = d.name.replace(/\s+with\s+.*/i, '').trim()
        }
      })
      setDishLabels(Object.keys(labels).length > 0 ? labels : null)
    }).catch(() => setDishLabels(null))
  }, [currentMenu])

  // Load the latest import from the database on mount (scoped to current menu)
  // This ensures switching menus doesn't show stale data from a different week
  useEffect(() => {
    if (!currentCity || !currentMenu) {
      setInitialLoading(false)
      setData(null)  // Clear stale data when no menu selected
      return
    }

    async function loadLatestImport() {
      try {
        const latest = await airtableAPI.getLatest(currentCity, currentMenu)
        // Backend returns null when no import exists for this menu — check for valid data
        if (latest && latest.meal_counts) {
          setData(latest)
          // Don't auto-update meal counts on load — only when user clicks Import.
          // Auto-updating here overwrites any manually saved meal counts.
        } else {
          setData(null)  // No import for this menu yet
        }
      } catch (err) {
        console.error('Failed to load latest import:', err)
        // Don't show error to user — just means no import exists yet
      } finally {
        setInitialLoading(false)
      }
    }

    loadLatestImport()
  }, [currentCity, currentMenu])

  async function handleImport(scope = null) {
    if (!currentCity) {
      showError('No city selected')
      return
    }

    setLoading(true)
    setLoadingScope(scope)
    setProgress(`Connecting to ${sourceLabel}...`)

    // The import runs as a background job on the server: analyzing a full week
    // of customizations with AI takes 1–2+ minutes, which is longer than a
    // browser will hold a single request open (this is what made the button
    // appear to hang / fail). We kick it off, poll for status, then load the
    // saved result — so the request itself is always short.
    const POLL_MS = 2500
    const MAX_WAIT_MS = 6 * 60 * 1000 // give up after 6 minutes

    try {
      const started = await airtableAPI.importOrders(currentCity, currentMenu, true, scope)
      // Another import for this city is still running; its result (and scope) would be read back as ours.
      if (started?.status === 'already_running') {
        throw new Error('Another import for this city is still running. Wait for it to finish, then import again.')
      }

      const startedAt = Date.now()
      const result = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          if (Date.now() - startedAt > MAX_WAIT_MS) {
            clearInterval(poll)
            reject(new Error('Import timed out — please try again'))
            return
          }
          try {
            const status = await airtableAPI.getImportStatus(currentCity)
            if (status.status === 'analyzing') {
              setProgress('Analyzing customizations with AI...')
            } else if (status.status === 'importing') {
              setProgress(`Importing orders from ${sourceLabel}...`)
            } else if (status.status === 'complete') {
              clearInterval(poll)
              // The interval is stopped, so a failure here must reject rather than wait forever.
              try {
                const latest = await airtableAPI.getLatest(currentCity, currentMenu)
                if (!latest || !latest.meal_counts) {
                  reject(new Error('Import finished but no data was found'))
                } else if (scope && (latest.scope || 'confirmed') !== scope) {
                  // Another import (e.g. the other button, in another tab) saved after this one.
                  reject(new Error(`Another ${latest.scope || 'confirmed'} import replaced this one. Import again.`))
                } else {
                  resolve(latest)
                }
              } catch (err) {
                reject(err)
              }
            } else if (status.status === 'error') {
              clearInterval(poll)
              reject(new Error(status.error || 'Import failed'))
            }
          } catch { /* transient poll error — keep waiting */ }
        }, POLL_MS)
      })

      setData(result)

      // No longer caching in localStorage — data is saved to database by backend
      // so all users see the same import

      // Show week info in success message
      const weekLabel = result.week_of ? ` for week of ${result.week_of}` : ''
      const scopeLabel = result.scope === 'paid' ? ' paid' : result.source === 'havn_os' ? ' confirmed' : ''
      success(`Imported ${result.order_count}${scopeLabel} orders${weekLabel} (${result.customization_count} with customizations)`)

      // Show any warnings (e.g., date mismatch between views)
      if (result.warnings?.length > 0) {
        result.warnings.forEach(w => showError(`⚠️ ${w}`))
      }

      // Update the meal count cards above with imported totals
      if (onImportRef.current && result.meal_counts) {
        console.log('Calling onImportMealCounts with:', Object.keys(result.meal_counts))
        onImportRef.current(result.meal_counts)
      }
    } catch (err) {
      showError('Import failed: ' + (err.message || 'Unknown error'))
      console.error('Import failed:', err)
    } finally {
      setLoading(false)
      setLoadingScope(null)
      setProgress('')
    }
  }

  const cityLabel = currentCity === 'dc' ? 'DC' : currentCity === 'sd' ? 'San Diego' : currentCity === 'miami' ? 'Miami' : currentCity === 'phoenix' ? 'Phoenix' : currentCity === 'chicago' ? 'Chicago' : currentCity

  return (
    <div className="animate-fade-in">
      {data?.warnings?.length > 0 && (
        <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 mb-5">
          {data.warnings.map((warning, index) => <p key={index}>{warning}</p>)}
        </div>
      )}
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pt-8 border-t border-havn-cream-300">
        <div>
          <h3 className="font-display text-xl font-semibold text-havn-green-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Kitchen Production Sheet
          </h3>
          <p className="text-sm text-havn-gray-500 mt-1">
            Import from {sourceLabel} to generate meal counts and customizations for the kitchen
          </p>
        </div>

        <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
          {/* Import status */}
          {data && !loading && (
            <span className="text-xs text-havn-gray-400 flex items-center gap-1">
              <Check className="w-3 h-3 text-green-500" />
              {data.order_count} {importScopeLabel(data)}orders · Imported {new Date(data.imported_at).toLocaleTimeString()}
            </span>
          )}

          {/* Print button */}
          {data && !loading && (
            <button
              onClick={() => openPrintView(data, dishLabels)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-havn-green-700 bg-white border border-havn-green-700 rounded-lg hover:bg-havn-green-700 hover:text-white transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          )}

          {/* Export for AI (.md) button — LLM-readable version */}
          {data && !loading && (
            <button
              onClick={() => {
                const md = buildProductionSheetMD(data, dishLabels)
                downloadMD(buildFilename('production-sheet', { city: data.city_id, menu: data.week_of }), md)
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-havn-green-700 bg-white border border-havn-green-700 rounded-lg hover:bg-havn-green-700 hover:text-white transition-colors"
              title="Download a markdown file optimized for feeding into an LLM (e.g. Claude Code)"
            >
              <FileCode className="w-4 h-4" />
              Export for AI
            </button>
          )}

          {importLocked && (
            <span className="text-xs font-medium text-havn-gray-500 flex items-center gap-1 whitespace-nowrap">
              <Lock className="w-3 h-3" />
              Counts locked
            </span>
          )}

          {/* Import buttons: Havn OS cities choose paid-only or every confirmed order */}
          {canScope ? (
            <>
              <ImportButton
                onClick={() => handleImport('paid')}
                disabled={loading || importLocked}
                busy={loading && loadingScope === 'paid'}
                label="Import paid"
                title={importLocked ? LOCKED_TITLE : 'Count only confirmed orders that are paid'}
              />
              <ImportButton
                onClick={() => handleImport('confirmed')}
                disabled={loading || importLocked}
                busy={loading && loadingScope === 'confirmed'}
                label="Import confirmed"
                title={importLocked ? LOCKED_TITLE : 'Count every confirmed order, paid or not'}
              />
            </>
          ) : (
            <ImportButton
              onClick={() => handleImport()}
              disabled={loading || importLocked}
              busy={loading}
              label={`Import from ${sourceLabel}`}
            />
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl border border-havn-cream-300 p-12 text-center">
          <RefreshCw className="w-10 h-10 text-havn-green-700 animate-spin mx-auto mb-4" />
          <p className="font-display text-lg font-semibold text-havn-green-900">{progress || 'Importing...'}</p>
          <p className="text-sm text-havn-gray-500 mt-2">
            Pulling orders from {cityLabel} {sourceLabel} and analyzing customizations...
          </p>
          <p className="text-xs text-havn-gray-400 mt-1">
            This may take 15-30 seconds
          </p>
        </div>
      )}

      {/* Results */}
      {data && !loading && (
        <>
          {/* Flagged Items Warning */}
          <FlaggedItems items={data.flagged_items} />

          {/* Meal Counts — Always visible, card-based layout */}
          <div className="mb-6">
            <MealCountsSection mealCounts={data.meal_counts} totals={data.totals} dishLabels={dishLabels} />
          </div>

          {/* Customizations Section — Collapsible */}
          <div className="bg-white rounded-xl border border-havn-cream-300 overflow-hidden">
            <button
              onClick={() => setCustomsExpanded(!customsExpanded)}
              className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-havn-cream-100/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-havn-green-700" />
                <span className="font-display text-lg font-semibold text-havn-green-900">
                  Customizations
                </span>
                <span className="text-sm text-havn-gray-400">
                  {data.customization_count || 0} orders with special instructions
                </span>
              </div>
              {customsExpanded ? <ChevronUp className="w-5 h-5 text-havn-gray-400" /> : <ChevronDown className="w-5 h-5 text-havn-gray-400" />}
            </button>
            {customsExpanded && (
              <div className="px-6 pb-6">
                <CustomizationsTable customizations={data.customizations} dishLabels={dishLabels} />
              </div>
            )}
          </div>
        </>
      )}

      {/* Initial Loading State (fetching from database) */}
      {initialLoading && !loading && (
        <div className="bg-white rounded-xl border border-havn-cream-300 p-12 text-center">
          <RefreshCw className="w-10 h-10 text-havn-green-700 animate-spin mx-auto mb-4" />
          <p className="font-display text-lg font-semibold text-havn-green-900">Loading...</p>
          <p className="text-sm text-havn-gray-500 mt-2">Checking for existing import data</p>
        </div>
      )}

      {/* Empty State */}
      {!data && !loading && !initialLoading && (
        <div className="bg-white rounded-xl border border-havn-cream-300 p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-havn-cream-200 flex items-center justify-center">
            <Download className="w-8 h-8 text-havn-gray-400" />
          </div>
          <h3 className="font-display text-lg font-semibold text-havn-gray-500">No Production Data</h3>
          <p className="text-sm text-havn-gray-400 mt-1 max-w-md mx-auto">
            {importLocked
              ? <>Counts for {cityLabel} are locked; ask Zeshan to refresh</>
              : canScope
              ? <>Click "Import paid" (paid orders only) or "Import confirmed" (every confirmed order) to pull this week's orders for {cityLabel} and generate</>
              : <>Click "Import from {sourceLabel}" to pull this week's orders for {cityLabel} and generate</>}
            {importLocked ? ' them.' : <>{' '}the meal count sheet and customizations list for the kitchen.</>}
          </p>
        </div>
      )}
    </div>
  )
}
